<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { NuxtError } from "#app";
import AppButton from "./components/Common/Buttons/AppButton.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { HttpStatusEnum } from "~/utils/enums/common.enums";

const props = defineProps<{
  error: NuxtError;
}>();

const authStore = useAuthStore();

/**
 * Which kind of 403 this is. `roleConflictPending` is raised only by the three paths that
 * detect a role *change* (useErrorHandler's 409, auth.global's revalidateRoles, the
 * focus-revalidate plugin); a route the user simply may not open — typing /settings in the
 * address bar, say — reaches `showError(forbiddenError())` through roles-guard or
 * auth.global's isRouteForbidden check with the flag untouched. The store is not persisted,
 * so a direct URL landing always starts at `false`.
 *
 * Latched on, never off: `handleGoHome` clears the flag immediately before `clearError`
 * navigates away, and following it back to `false` would flash the plain-403 wording for the
 * frames in between. The watcher still catches a conflict raised while this page is already
 * up — the focus-revalidate plugin fires on visibilitychange wherever the user is, and the
 * error page is re-asserted rather than remounted, so setup does not run again.
 */
const isRoleConflict = ref(authStore.roleConflictPending);

watch(
  () => authStore.roleConflictPending,
  (pending) => {
    if (pending) isRoleConflict.value = true;
  },
);

/**
 * 403 and 404 are not technical failures: both are reached on purpose by the guards (a downgraded
 * role landing on a page it can no longer open, a parcours link opened by a company without the
 * feature), so each needs its own wording rather than the generic "a problem occurred".
 */
const content = computed(() => {
  if (props.error?.statusCode === HttpStatusEnum.FORBIDDEN) {
    /**
     * The roles just changed and the session has already been re-synced to the new ones, so
     * this is a "here is what changed, your space is elsewhere" screen rather than a denial —
     * hence the informative blue rather than the red of a real failure.
     */
    if (isRoleConflict.value) {
      return {
        icon: "refresh",
        badgeClass: "bg-light-blue-color text-primary-blue-color",
        title: "Vos droits ont été modifiés",
        message: "Votre rôle vient d'être modifié. Retournez à l'accueil pour accéder à votre nouvel espace.",
      };
    }

    // Nothing changed: the user simply never had access to this page.
    return {
      icon: "lock",
      badgeClass: "bg-light-red-color text-primary-red-color",
      title: "Accès non autorisé",
      message: "Vous n'avez pas les droits nécessaires pour accéder à cette page.",
    };
  }

  if (props.error?.statusCode === HttpStatusEnum.NOT_FOUND) {
    return {
      icon: "search",
      badgeClass: "bg-lightest-light-grey-color text-medium-grey-color",
      title: "Cette page n'existe pas",
      message:
        "Le lien que vous avez suivi ne mène à aucune page, ou son contenu n'est pas disponible pour votre entreprise.",
    };
  }

  return {
    icon: "warning_error",
    badgeClass: "bg-light-red-color text-primary-red-color",
    title: "Une erreur est survenue",
    message:
      "Un problème technique nous empêche d'afficher cette page. Réessayez dans un instant ou revenez à l'accueil.",
  };
});

const handleGoHome = () => {
  // Acknowledge a pending role-downgrade conflict so auth.global stops re-asserting
  // the error page on the redirect navigation below.
  authStore.roleConflictPending = false;
  const redirect = authStore.isLoggedIn() ? "/home" : "/";
  clearError({ redirect });
};
</script>

<template>
  <div class="flex items-center justify-center min-h-screen px-4 bg-lightest-blue-color">
    <div
      class="flex flex-col items-center text-center w-full max-w-[440px] px-10 py-12 bg-light-color rounded-2xl shadow-[shadow:var(--shadow-floating)]"
    >
      <div class="flex items-center justify-center w-20 h-20 mb-6 rounded-full" :class="content.badgeClass">
        <SvgIcon :name="content.icon" class="w-8 h-8" />
      </div>

      <h1 class="heading_1 text-primary-color mb-3">{{ content.title }}</h1>

      <p class="body_regular text-secondary-color mb-8 max-w-[340px]">{{ content.message }}</p>

      <!-- The action navigates home, it does not reload the failed route — keep the label honest. -->
      <AppButton label="Retour à l'accueil" icon="home" @click="handleGoHome" />
    </div>
  </div>
</template>
